import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Button } from "@postdaily/ui/components/button";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { Spinner } from "@postdaily/ui/components/spinner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { CalendarClock } from "lucide-react";
import { parseAsString, useQueryStates } from "nuqs";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { BillingPlanComparison } from "@/components/billing/billing-plan-comparison";
import { BillingPlanHero } from "@/components/billing/billing-plan-hero";
import { BillingUsageGrid } from "@/components/billing/billing-usage-grid";
import { PlanChangeDialog } from "@/components/billing/plan-change-dialog";
import { PageHeader } from "@/components/page-header";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { clearCheckoutIntent, getCheckoutIntent } from "@/lib/checkout-intent";
import {
  followChoosePlanResult,
  type PlanChangeQuote,
} from "@/lib/choose-plan";
import { useUpgradeDialogStore } from "@/stores/upgrade-dialog-store";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/settings/billing")({
  component: RouteComponent,
});

function RouteComponent() {
  const { currentWorkspace, setCurrentWorkspaceId, workspaces } =
    useCurrentWorkspace();
  // The past-due banner and the AI connector link here with the workspace
  // that pays, which can differ from the one open. Switch only to a workspace
  // the person belongs to, and hold the page until it lands so the portal
  // never opens for the wrong subscription. Same as /channels/connect.
  const [{ workspace: linkedWorkspaceId }] = useQueryStates({
    workspace: parseAsString,
  });
  const linkedWorkspace = linkedWorkspaceId
    ? workspaces.find((workspace) => workspace.id === linkedWorkspaceId)
    : undefined;

  useEffect(() => {
    if (linkedWorkspace && currentWorkspace?.id !== linkedWorkspace.id) {
      setCurrentWorkspaceId(linkedWorkspace.id);
    }
  }, [currentWorkspace?.id, linkedWorkspace, setCurrentWorkspaceId]);

  if (
    !currentWorkspace ||
    (linkedWorkspace && currentWorkspace.id !== linkedWorkspace.id)
  ) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          size="lg"
          title="Billing"
          description="Plan, usage & subscription for this workspace."
        />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  return <BillingPanel workspace={currentWorkspace} />;
}

function BillingPanel({
  workspace,
}: {
  workspace: NonNullable<
    ReturnType<typeof useCurrentWorkspace>["currentWorkspace"]
  >;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { workspaces } = useCurrentWorkspace();
  const billingState = useQuery(
    trpc.billing.state.queryOptions({ workspaceId: workspace.id }),
  );
  // Live plan catalog (from Polar) — supplies the current plan's price and the
  // upsell target's price for the hero. Enforcement limits come from the state.
  const plans = useQuery(trpc.billing.listPlans.queryOptions());
  const openUpgradeDialog = useUpgradeDialogStore(
    (store) => store.openUpgradeDialog,
  );
  const [quote, setQuote] = useState<PlanChangeQuote | null>(null);
  // A plan picked on the public pricing page before sign-up is resumed here;
  // analytics should still say it was picked on pricing.
  const resumedFromPricing = useRef(false);
  const choosePlan = useMutation(
    trpc.billing.choosePlan.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: (result) => {
        setQuote(
          followChoosePlanResult(
            result,
            resumedFromPricing.current ? "pricing" : "billing",
          ),
        );
      },
    }),
  );
  const openPortal = useMutation(
    trpc.billing.openPortal.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: (portal) => {
        window.location.assign(portal.portalUrl);
      },
    }),
  );
  const refreshState = useMutation(
    trpc.billing.refreshState.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: async () => {
        toast.success("Billing state refreshed");
        await queryClient.invalidateQueries(
          trpc.billing.state.queryFilter({ workspaceId: workspace.id }),
        );
      },
    }),
  );
  const cancelScheduledChange = useMutation(
    trpc.billing.cancelScheduledChange.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: async () => {
        toast.success("Your plan stays as it is.");
        await queryClient.invalidateQueries(
          trpc.billing.state.queryFilter({ workspaceId: workspace.id }),
        );
      },
    }),
  );
  const canManageBilling =
    workspace.role === "owner" || workspace.role === "admin";

  // Resume a checkout intent captured on the public pricing page. Fires once,
  // after billing state has loaded, and only if the intended product isn't the
  // one already in place. A workspace that pays gets the plan-change confirm,
  // not a change. Cleared unconditionally so it never loops.
  const checkoutIntentHandled = useRef(false);
  useEffect(() => {
    if (checkoutIntentHandled.current) {
      return;
    }

    const state = billingState.data;
    if (!state) {
      return;
    }

    const intent = getCheckoutIntent();
    if (!intent) {
      return;
    }

    checkoutIntentHandled.current = true;
    clearCheckoutIntent();

    if (!canManageBilling) {
      toast.info("Ask a workspace owner or admin to upgrade the plan.");
      return;
    }

    const alreadyOnIt = state.productId
      ? state.productId === intent.productId
      : state.effectivePlanCode === intent.planGroup;

    if (alreadyOnIt || state.planSource === "owner") {
      return;
    }

    resumedFromPricing.current = true;
    choosePlan.mutate({
      planGroup: intent.planGroup,
      productId: intent.productId,
      returnPath: "/settings/billing",
      workspaceId: workspace.id,
    });
  }, [billingState.data, canManageBilling, choosePlan, workspace.id]);

  if (billingState.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          size="lg"
          title="Billing"
          description={`Plan, usage & subscription for ${workspace.name}.`}
        />
        <Skeleton className="h-56 w-full rounded-2xl" />
        <Skeleton className="h-72 w-full rounded-2xl" />
      </div>
    );
  }

  if (billingState.isError) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          size="lg"
          title="Billing"
          description={`Plan, usage & subscription for ${workspace.name}.`}
        />
        <Alert variant="destructive">
          <AlertTitle>Billing unavailable</AlertTitle>
          <AlertDescription>{billingState.error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const state = billingState.data;
  const currentPlanEntry = plans.data?.find(
    (plan) => plan.planGroup === state.effectivePlanCode,
  );
  const targetPlanEntry = state.upgradeTarget
    ? plans.data?.find((plan) => plan.planGroup === state.upgradeTarget?.code)
    : undefined;
  const { pendingChange } = state;

  function handleUpgrade() {
    const targetCode = state.upgradeTarget?.code;
    openUpgradeDialog(
      targetCode && targetCode !== "free"
        ? { highlightPlan: targetCode }
        : undefined,
    );
  }

  function handleManage() {
    openPortal.mutate({ workspaceId: workspace.id });
  }

  function handleRefresh() {
    refreshState.mutate({ workspaceId: workspace.id });
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        size="lg"
        title="Billing"
        description={`Plan, usage & subscription for ${workspace.name}.`}
      />

      {pendingChange ? (
        <div className="flex flex-col gap-3 rounded-2xl border border-primary/25 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <CalendarClock className="mt-0.5 size-4 shrink-0 text-primary" />
            <div className="flex flex-col gap-0.5">
              <p className="font-medium text-sm">
                Your plan changes to {pendingChange.planName} on{" "}
                {dayjs(pendingChange.effectiveAt).format("MMM D, YYYY")}
              </p>
              <p className="text-muted-foreground text-sm">
                You keep {state.planName} and everything in it until then.
              </p>
            </div>
          </div>
          {canManageBilling ? (
            <Button
              className="shrink-0"
              disabled={cancelScheduledChange.isPending}
              onClick={() =>
                cancelScheduledChange.mutate({ workspaceId: workspace.id })
              }
              size="sm"
              type="button"
              variant="outline"
            >
              {cancelScheduledChange.isPending ? (
                <Spinner data-icon="inline-start" />
              ) : null}
              Keep {state.planName}
            </Button>
          ) : null}
        </div>
      ) : null}

      <BillingPlanHero
        canManageBilling={canManageBilling}
        currentPlanEntry={currentPlanEntry}
        isPortalPending={openPortal.isPending}
        isRefreshPending={refreshState.isPending}
        isUpgradePending={choosePlan.isPending}
        onChangePlan={() =>
          openUpgradeDialog({ source: "billing_change_plan" })
        }
        onManage={handleManage}
        onRefresh={handleRefresh}
        onUpgrade={handleUpgrade}
        state={state}
        targetPlanEntry={targetPlanEntry}
      />

      <BillingUsageGrid state={state} />

      {state.canUpgrade &&
      state.upgradeTarget &&
      state.planSource !== "owner" ? (
        <BillingPlanComparison
          canManageBilling={canManageBilling}
          isUpgradePending={choosePlan.isPending}
          onUpgrade={handleUpgrade}
          state={state}
        />
      ) : null}

      {!canManageBilling ? (
        <p className="text-muted-foreground text-sm">
          Your role can view billing but cannot change the plan. Ask a workspace
          owner or admin to manage the subscription.
        </p>
      ) : null}

      <PlanChangeDialog
        location="billing"
        onClose={() => setQuote(null)}
        quote={quote}
        showWorkspace={workspaces.length > 1}
      />
    </div>
  );
}
